import { createContext, useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { SearchModel } from "../../models/SearchModel";
import { fetchAPI, paginateItems } from "../../Helpers/utils";

//----------------------------------------
// REDUCER
export enum SearchActionEnum {
  SET_PAGESIZE,
  SET_RESULTS,
  GET_PAGED_RESULTS
}

export type SearchState = {
  results: SearchModel[],
  pagedResults: SearchModel[],
  pageSize: number,
  currentPage: number,
  totalRecords: number
}

const initialSearchState = {
  results: [],
  pagedResults: [],
  pageSize: 20,
  currentPage: 1,
  totalRecords: 0
}

export type SearchAction = |
{ action: SearchActionEnum.SET_PAGESIZE | SearchActionEnum.GET_PAGED_RESULTS, payload: number }
| { action: SearchActionEnum.SET_RESULTS, payload: SearchModel[] }
export const SearchStateReducer = (state: SearchState, event: SearchAction): SearchState => {
  switch (event.action) {
    case SearchActionEnum.SET_PAGESIZE:
      return {
        ...state,
        pageSize: event.payload
      }
    
    case SearchActionEnum.SET_RESULTS:
      return {
        ...state,
        currentPage: 1,
        results: [...event.payload],
        pagedResults: [...paginateItems([...event.payload], 1, state.pageSize)],
        totalRecords: event.payload.length
      }

    case SearchActionEnum.GET_PAGED_RESULTS:
      return {
        ...state,
        currentPage: event.payload,
        pagedResults: [...paginateItems([...state.results], event.payload, state.pageSize)]
      }
    
    default:
      return initialSearchState;
  }
}

//----------------------------------------
// CONTEXT
export type SearchContextType = {
  results: SearchModel[],
  searchTerm: string,
  totalRecords: number,
  currentPage: number,
  pageSize: number,

  loadItems: (page: number) => void,
  search: (keyword: string) => void
}

export const SearchContext = createContext<SearchContextType | null>(null);

//----------------------------------------
// PROVIDER
export type SearchProviderModel = {
  children: React.ReactNode,
  pageSize: number
}

export default function SearchProvider({ children, pageSize }: SearchProviderModel) {
  const [state, dispatch] = useReducer(SearchStateReducer, initialSearchState);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');
    dispatch({ action: SearchActionEnum.SET_PAGESIZE, payload: pageSize });
    if (query) {
      setSearchTerm(query);
    }
  }, []);

  useEffect(() => {
    async function search() {
      const data = await fetchAPI('/api/data/search', {
        query: searchTerm,
        pageSize: 100000,
        page: 1
      }) as any;

      dispatch({ action: SearchActionEnum.SET_RESULTS, payload: data.items as SearchModel[] });
    }

    if (searchTerm.length) {
      search();
    }
  }, [searchTerm]);

  const loadItems = useCallback((page: number) => {
    dispatch({ action: SearchActionEnum.GET_PAGED_RESULTS, payload: page });
  }, []);

  const search = useCallback((keyword: string) => {
    setSearchTerm(keyword);
  }, []);

  const values = useMemo(() => {
    return {
      results: state.pagedResults,
      searchTerm,
      totalRecords: state.totalRecords,
      currentPage: state.currentPage,
      pageSize: state.pageSize,

      loadItems,
      search
    }
  }, [
    state,
    searchTerm,

    loadItems,
    search
  ]);

  return (
    <SearchContext.Provider value={values}>
      {children}
    </SearchContext.Provider>
  )
}